import { Localized } from "@fluent/react"
import { Box, Checkbox, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { unwrapResult } from "@reduxjs/toolkit"
import { MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AppId } from "../../app/appTypes"
import { DocumentNumberBox } from "../../app/components/DocumentNumberBox"
import SmallStatusBadge from "../../app/components/SmallStatusBadge"
import { useAppDispatch } from "../../app/hooks"
import { LoadingContainer } from "../../app/LoadingContainer"
import { addCacheEntry, tryGetCacheEntry } from "../documents/cacheSlice"
import { ReqCriticalityLabel } from "./ReqCriticalityLabel"
import { RequirementDescription } from "./RequirementDescription"
import { RequirementsTabs } from "./RequirementsPanel"
import { loadUrsOnly, Urs, UrsGroup, URSRequirement } from "./UrsSlice"

export interface UrsRequirementBrowserProps {
    ursId: AppId
    selectedItem: URSRequirement | null
    setSelectedItem: (item: URSRequirement | null) => void
}

export const UrsRequirementBrowser = (props: UrsRequirementBrowserProps) => {
    const { ursId } = props
    const dispatch = useAppDispatch()

    const [urs, setUrs] = useState<Urs | null>(null)

    useEffect(() => {
        const cached = tryGetCacheEntry(ursId, 'urs')
        switch (cached.tag) {
            case 'ok':
                setUrs((cached.data as Urs))
                break;
            case 'expired':
            case 'nothing':
                dispatch(loadUrsOnly(ursId))
                    .then(unwrapResult)
                    .then(data => {
                        setUrs(data)
                        dispatch(addCacheEntry({
                            id: ursId,
                            data,
                            type: 'urs',
                        }))
                    })
                    .catch(error => console.log('Error loading urs:' ,error))
                break;
        }
    }, [])

    if (urs === null) {
        return <LoadingContainer />
    } else {
        return <BrowserWithData {...props} urs={urs} />
    }
}

const BrowserWithData = (props: UrsRequirementBrowserProps & { urs: Urs }) => {
    const { urs } = props
    const [contextTypeId, setContextTypeId] = useState<AppId | null>(() => {
        if (urs.types.length > 0) {
            const [someItem] = urs.types
            return someItem.id
        } else {
            return null
        }
    })
    const tableRef = useRef<null | HTMLDivElement>(null)

    const handleContextChange = useCallback((id: AppId | null) => setContextTypeId(id), [])

    const contextType = urs.types.find(x => x.id === contextTypeId)

    const mask = useMemo(() => {
        return <>
            <TableCell></TableCell>
            <TableCell>Loading data...</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
        </>
    }, [])

    // TODO: if no context render just flat table with requirements
    if (contextType === undefined) return <span>No type</span>

    return <Box sx={{
        display: 'flex',
        gap: 1,
    }}>
        <RequirementsTabs urs={urs} typeId={contextTypeId ?? ''} setContextTypeId={handleContextChange} />
        <TableContainer>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ minWidth: 50, width: 80, maxWidth: 80 }}>
                        <Typography component="span" variant="subtitle2" color="textSecondary">
                            <Localized id="urs-item-lp">Lp</Localized>
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography component="span" variant="subtitle2" color="textSecondary">
                            <Localized id="urs-item-name">Nazwa</Localized>
                        </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}>
                        <Typography component="span" variant="subtitle2" color="textSecondary">
                            <Localized id="urs-item-criticality">Krytyczność</Localized>
                        </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 80, width: 80, maxWidth: 80, justifyContent: 'flex-end' }}></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>

                {contextType.requirements.map(requirement =>
                    <>
                        <URSRequirementRowInternal
                            {...props}
                            key={requirement.id}
                            requirement={requirement}
                            tableRef={tableRef}
                            // mask={mask}
                        />
                        {requirement.childRequirements.map(childReq =>
                            <URSRequirementRowInternal
                                {...props}
                                key={childReq.id}
                                requirement={childReq}
                                tableRef={tableRef}
                                // mask={mask}
                            />
                        )}
                    </>
                )}
                {contextType.groups.map(group => <>
                    <Group group={group} />
                    {group.requirements.map(requirement =>
                        <>
                            <URSRequirementRowInternal
                                {...props}
                                key={requirement.id}
                                requirement={requirement}
                                tableRef={tableRef}
                                // mask={mask}
                            />
                            {requirement.childRequirements.map(childReq =>
                                <URSRequirementRowInternal
                                    {...props}
                                    key={childReq.id}
                                    requirement={childReq}
                                    tableRef={tableRef}
                                    // mask={mask}
                                />
                            )}
                        </>
                    )}
                </>)}

            </TableBody>
        </TableContainer>
    </Box>
}

interface URSRequirementRowProps extends UrsRequirementBrowserProps {
  requirement: URSRequirement
  tableRef: MutableRefObject<null | HTMLDivElement>
}
const URSRequirementRow = (props: URSRequirementRowProps & { mask: ReactNode }) => {
  const { requirement, tableRef, mask } = props
  const rowRef = useRef<null | HTMLTableRowElement>(null)
  const observer = useRef<IntersectionObserver | null>(null)

  const [shown, setShown] = useState(false)

  useEffect(() => {
    if (tableRef.current && rowRef.current) {
      observer.current = new IntersectionObserver((entries) => {
        if (entries.length === 1) {
          const element = entries[0]
          if (element.isIntersecting) {
            if (observer.current) {
              observer.current.unobserve(element.target)
              observer.current.disconnect()
            }
            setShown(true)
          }
        }
      }, {
        root: tableRef.current,
        threshold: .2,
      })
      observer.current.observe(rowRef.current)
    }
  }, [])

  const content = shown ? <Memoized {...props}></Memoized> : mask

  return <TableRow ref={rowRef} id={requirement.guid} className="target:shadow-lg target:bg-slate-100 target:shadow-inner" sx={{
    ...(requirement.status === 'DELETED' && {
      textDecoration: 'line-through',
      color: 'red'
    })
  }}>
    {content}
  </TableRow>
}

const Memoized = (props: URSRequirementRowProps) => {
    const { requirement } = props

    const row = useMemo(() => {
        return <URSRequirementRowInternal {...props}></URSRequirementRowInternal>
    }, [requirement.modifyDate])

    return <>{row}</>
}

const URSRequirementRowInternal = (props: URSRequirementRowProps) => {
  const { requirement, selectedItem, setSelectedItem } = props

    const handleSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedItem(requirement)
        } else {
            setSelectedItem(null)
        }
    };

  return <TableRow>
    <TableCell
      sx={{
        paddingRight: requirement.parentRequirementId ? '20px' : '2px',
        textAlign: requirement.parentRequirementId ? 'right' : 'left',
        backgroundColor: requirement.parentRequirementId ? '#fafafa' : 'inherit',
        mminWidth: 110, width: 110, maxWidth: 110
      }}
    >
      <DocumentNumberBox value={requirement.numeration} />
    </TableCell>
    <TableCell sx={{
      ['& p img']: {
        maxWidth: '600px',
      },
    }}>
      {/* {code && <Chip label={code} size='small' />} */}
      <SmallStatusBadge label={requirement.status} />
      <div>
        <RequirementDescription description={requirement.name} />
      </div>
    </TableCell>
    <TableCell>{<ReqCriticalityLabel criticality={requirement.criticality ?? ""} />}</TableCell>
    <TableCell>
            <Checkbox checked={selectedItem?.id === requirement.id} onChange={handleSelectionChange} />
    </TableCell>
  </TableRow>
}

export const Group = (props: { group: UrsGroup }) => {
    const { group } = props

    return <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
        <TableCell colSpan={4}>{`${group.numeration}. ${group.name}`}</TableCell>
    </TableRow>

}
